import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useNeuroMessages } from './MessageContext';
import NeuroMessage from './NeuroMessage';
import { useQuestionContext } from './QuestionContext.jsx';
import Tooltip from './Tooltip.jsx';
import { QuestionShape } from '../utils';
import { renderQuestionErrors } from './ValidationMessages';
import {useGuideConfig} from '../hooks/useGuideConfig';

const position = {
  afterLabel: 'afterLabel',
  afterSubtext: 'afterSubtext',
  beforeLabel: 'beforeLabel',
};

const QuestionHeader = (props) => {
  const { question, className } = props;
  const componentClass = 'question';
  const questionContext = useQuestionContext();
  const guideConfig = useGuideConfig();

  const classes = classNames(className, 'question__text');
  const messageContext = useNeuroMessages();
  const attributes = question?.attributes;
  const tooltipProps = attributes?.tooltipProps;
  const ErrorTag = guideConfig?.errorValidations?.options?.inlineTag;
  const errorProps =
    attributes?.error?.props ||
    questionContext.questionTypes?.attributes?.error?.props ||
    guideConfig?.errorValidations?.props;
  const errorPosition =
    guideConfig?.errorValidations?.options?.inlinePosition ||
    position.beforeLabel;
  const errorIcon = guideConfig?.errorValidations?.options?.icon;

  const label = <NeuroMessage id={question.appkey} shouldHideIfNotFound={true} key={question.appkey}/>;
  const ariaLabel = messageContext.getMessage(question.appkey);

  const tooltip = (
    <NeuroMessage id={`${question.appkey}.tooltip`} key={`${question.appkey}.tooltip`} shouldHideIfNotFound={true}>
      { () =>
        <Tooltip className={`${componentClass}__tooltip`} messageId={`${question.appkey}.tooltip`}{...tooltipProps} />
      }
    </NeuroMessage>
  );

  const customTooltipRender = questionContext.renderCustomTooltip(question, null,
    {...tooltipProps, className: `${componentClass}__tooltip` }
  );

  const error = () =>
    ErrorTag ? (
      <ErrorTag
        key={`${question.appkey}.error`}
        className={`${componentClass}__error error`}
        {...errorProps}
      >
        {errorIcon}
        {renderQuestionErrors(question)}
      </ErrorTag>
    ) : (
      <Tooltip
        type="error"
        key={`${question.appkey}.error`}
        className={`${componentClass}__tooltip error`}
        messageId={question.appkey}
        iconRender={<i className="fa fa-exclamation-circle"/>}
        content={<span>{renderQuestionErrors(question)}</span>}
        {...errorProps}
      />
    );

  const errorNode =
    question.errors &&
    (questionContext.questionTypes?.attributes?.error?.node ||
      error());

  const getErrorPosition = () => {
    switch (errorPosition) {
      case `${position.afterLabel}`:
        return [label, tooltip, customTooltipRender, errorNode];
      case `${position.afterSubtext}`:
        return [label, tooltip, customTooltipRender];
      case `${position.beforeLabel}`:
      default:
        return [errorNode, label, tooltip, customTooltipRender];
    }
  };

  return (
    <legend className={classes} aria-label={ariaLabel} tabIndex="-1">
      <div>{getErrorPosition()}</div>
      <NeuroMessage id={`${question.appkey}.subtext`} key={`${question.appkey}.subtext`} shouldHideIfNotFound={true} tagName="div" className="subtext"/>
      {errorPosition === 'afterSubtext' && errorNode}
    </legend>
  );
};

QuestionHeader.propTypes = {
  className: PropTypes.string,
  question: QuestionShape,
};

export default QuestionHeader;
